import React, { useState } from "react";
import { Typography, Box, TextField } from "@mui/material";
import { Card, CardHeader, Divider } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import PrimaryButton from "../../../widgets/Buttons/PrimaryButton";
import SecondaryButton from "../../../widgets/Buttons/SecondaryButton";
import CustomTable from "../../../widgets/Table/CustomTable";
import CustomModal from "../../../widgets/Modal/CustomModal";
import ErrorSnackbar from "../../../widgets/Snackbar/ErrorSnackbar";
import SuccessSnackbar from "../../../widgets/Snackbar/SuccessSnackbar";
import { useConnectCall } from "../../../hooks/useConnectCall";
import { useUpsertNotes } from "../../../hooks/useUpsertNotes";
import tokenUtils from "utils/tokenUtils";
import styles from "../StudentDetails.module.scss";

const maskPhoneNumber = (phoneStr) => {
  if (!phoneStr) return "-";
  const str = String(phoneStr);
  if (str.length >= 10) return str.slice(0, 2) + "XXXXX" + str.slice(7);
  if (str.length > 2) return "x".repeat(str.length - 2) + str.slice(-2);
  return str;
};

const StudentInfoCard = ({ studentData, phone, features, verticalInfo }) => {
  const {
    mutate: connectCall,
    isPending: isConnectCallPending,
    isLoading: isConnectCallLoading,
  } = useConnectCall();
  const isCalling = isConnectCallPending || isConnectCallLoading;

  const {
    mutate: upsertNotes,
    isPending: isUpsertNotesPending,
    isLoading: isUpsertNotesLoading,
  } = useUpsertNotes();
  const isUpsertingNotes = isUpsertNotesPending || isUpsertNotesLoading;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [notesModal, setNotesModal] = useState({
    open: false,
    text: "",
    callId: "",
  });
  const [errorSnackbar, setErrorSnackbar] = useState({
    open: false,
    message: "",
  });
  const [successSnackbar, setSuccessSnackbar] = useState({
    open: false,
    message: "",
  });

  const {
    first_name,
    last_name,
    uam_id,
    phone: responsePhone,
    admission_number,
    grade_id,
  } = studentData;

  const handleConnectCall = () => {
    const callerId = tokenUtils.getUserId();
    setConfirmOpen(false);

    connectCall(
      {
        caller_id: String(callerId),
        callee_id: String(studentData?.uam_id),
        callee_vertical_id: String(verticalInfo?.id || ""),
      },
      {
        onSuccess: (res) => {
          const status = res?.status || res?.data?.status;
          if (status === "SUCCESS") {
            setSuccessSnackbar({
              open: true,
              message: "Call connected successfully.",
            });
            const callId =
              res?.call_id ||
              res?.callId ||
              res?.data?.call_id ||
              res?.data?.callId ||
              "";
            setNotesModal({ open: true, text: "", callId });
          } else {
            setErrorSnackbar({
              open: true,
              message:
                "Failed to connect call.",
            });
          }
        },
        onError: (err) => {
          setErrorSnackbar({
            open: true,
            message:  "Failed to connect call.",
          });
        },
      },
    );
  };

  const handleUpsertNotes = () => {
    upsertNotes(
      { call_id: notesModal.callId, notes: notesModal.text },
      {
        onSuccess: () => {
          setSuccessSnackbar({
            open: true,
            message: "Notes added successfully.",
          });
          setNotesModal({ open: false, text: "", callId: "" });
        },
        onError: (err) => {
          setErrorSnackbar({
            open: true,
            message:  "Failed to add notes.",
          });
        },
      },
    );
  };

  const studentInfoColumns = [
    {
      id: "label",
      label: "Field",
      cellSx: {
        width: "38%",
        fontWeight: 600,
        color: "#0f172a",
        borderRight: "1px solid #e2e8f0",
      },
    },
    {
      id: "value",
      label: "Value",
      render: (row) => (
        <div className={styles.phoneCell}>
          <span className={row.capitalize ? styles.capitalize : ""}>
            {row.value}
          </span>
          {row.action && (
            <div className={styles.callBtnWrapper}>{row.action}</div>
          )}
        </div>
      ),
    },
  ];

  const studentInfoData = [
    { label: "First Name", value: first_name || "—", capitalize: true },
    { label: "Last Name", value: last_name || "—", capitalize: true },
    { label: "UAM ID", value: uam_id || "—" },
    { label: "Admission No.", value: admission_number || "—" },
    {
      label: "Masked Phone",
      value: maskPhoneNumber(responsePhone || phone),
      action: features.call ? (
        <PrimaryButton
          onClick={() => setConfirmOpen(true)}
          isLoading={isCalling}
          startIcon={!isCalling ? <CallIcon /> : null}
          sx={{ height: "35px", px: 2, width: "100%", fontSize: "0.85rem" }}
        >
          {isCalling ? "Calling" : "Call"}
        </PrimaryButton>
      ) : null,
    },
    { label: "Grade ID", value: grade_id || "—" },
  ];

  return (
    <>
      <Card
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardHeader
          title="Student Information"
          titleTypographyProps={{
            fontSize: "1.05rem",
            fontWeight: 600,
            color: "#0f172a",
            m: 0,
          }}
          sx={{ backgroundColor: "#ffffff", padding: "16px 20px" }}
        />
        <Divider sx={{ borderColor: "#e2e8f0" }} />
        <div className={styles.tableContainer}>
          <CustomTable
            columns={studentInfoColumns}
            data={studentInfoData}
            hideHeader
            sx={{ border: "none", borderRadius: 0 }}
          />
        </div>
      </Card>

      <CustomModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm Action"
        maxWidth="xs"
        actions={
          <>
            <SecondaryButton onClick={() => setConfirmOpen(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleConnectCall}>Confirm</PrimaryButton>
          </>
        }
      >
        <Typography sx={{ color: "#334155", fontSize: "1rem" }}>
          Are you sure you want to proceed with <strong>Connect Call</strong>?
        </Typography>
      </CustomModal>

      <CustomModal
        open={notesModal.open}
        onClose={() => setNotesModal({ open: false, text: "", callId: "" })}
        title="Add Call Notes"
        maxWidth="sm"
        actions={
          <>
            <SecondaryButton
              onClick={() =>
                setNotesModal({ open: false, text: "", callId: "" })
              }
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleUpsertNotes}
              isLoading={isUpsertingNotes}
            >
              Add notes
            </PrimaryButton>
          </>
        }
      >
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Enter call notes (max 200 characters)..."
            value={notesModal.text}
            inputProps={{ maxLength: 200 }}
            onChange={(e) =>
              setNotesModal((prev) => ({ ...prev, text: e.target.value }))
            }
            helperText={`${notesModal.text.length}/200 characters`}
            FormHelperTextProps={{ sx: { textAlign: "right", marginRight: 0 } }}
          />
        </Box>
      </CustomModal>

      <ErrorSnackbar
        open={errorSnackbar.open}
        message={errorSnackbar.message}
        onClose={() => setErrorSnackbar({ open: false, message: "" })}
      />
      <SuccessSnackbar
        open={successSnackbar.open}
        message={successSnackbar.message}
        onClose={() => setSuccessSnackbar({ open: false, message: "" })}
      />
    </>
  );
};

export default StudentInfoCard;
